import { FormEvent, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { api, brl } from "../lib/api";
import { useUser } from "../hooks/useUser";

// Captura o codigo de afiliado de ?aff=XYZ na URL e persiste (atribuicao
// dura mesmo se o user navegar antes de comprar). Idempotente: se ?aff= estiver
// presente, atualiza; senao, retorna o valor ja guardado.
const AFFILIATE_STORAGE_KEY = "icl_affiliate_code";
function readAffiliateCode(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("aff");
    if (fromUrl) {
      localStorage.setItem(AFFILIATE_STORAGE_KEY, fromUrl.slice(0, 40));
    }
    return localStorage.getItem(AFFILIATE_STORAGE_KEY);
  } catch {
    return null;
  }
}

// Captura ?offer=CODE (link de oferta, estilo Hotmart) e persiste na sessao pra
// sobreviver ao bounce de login. Consumido uma vez ao iniciar o checkout dirigido
// pela oferta (clearPendingOffer).
const PENDING_OFFER_KEY = "icl_pending_offer";
function readPendingOffer(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("offer");
    if (fromUrl) sessionStorage.setItem(PENDING_OFFER_KEY, fromUrl.slice(0, 80));
    return sessionStorage.getItem(PENDING_OFFER_KEY);
  } catch {
    return null;
  }
}
function clearPendingOffer(): void {
  try {
    sessionStorage.removeItem(PENDING_OFFER_KEY);
  } catch {
    /* noop */
  }
}

// Cartões de teste pro modal. Aplicam no checkout hosted/Elements (digitar o
// número). Validade: qualquer data futura; CVC: 3 dígitos quaisquer; CEP: qualquer.
// Numeração alinhada com docs/TEST-CARDS.md.
const TEST_CARD_GROUPS: {
  title: string;
  cards: { number: string; effect: string }[];
}[] = [
  {
    title: "Sucesso",
    cards: [{ number: "4242 4242 4242 4242", effect: "Aprovado na hora" }],
  },
  {
    title: "Autenticação (3DS)",
    cards: [
      { number: "4000 0025 0000 3155", effect: "Exige autenticação 3DS" },
      { number: "4000 0000 0000 3220", effect: "Exige 3DS v2" },
    ],
  },
  {
    title: "Recusas",
    cards: [
      { number: "4000 0000 0000 0002", effect: "Recusado (genérico)" },
      { number: "4000 0000 0000 9995", effect: "Saldo insuficiente" },
      { number: "4000 0000 0000 0069", effect: "Cartão expirado" },
      { number: "4000 0000 0000 0127", effect: "CVC incorreto" },
    ],
  },
  {
    title: "Radar / antifraude",
    cards: [
      { number: "4100 0000 0000 0019", effect: "Bloqueado pelo Radar (risco altíssimo)" },
      { number: "4000 0000 0000 9235", effect: "Aprova, mas risco elevado" },
      { number: "4000 0000 0000 0259", effect: "Aprova e vira disputa fraudulenta" },
      { number: "4000 0000 0000 0341", effect: "Aprova e vira chargeback" },
    ],
  },
];

function TestCardsModal({ onClose }: { onClose: () => void }) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (n: string) => {
    navigator.clipboard?.writeText(n.replace(/\s/g, ""));
    setCopied(n);
    setTimeout(() => setCopied((c) => (c === n ? null : c)), 1200);
  };
  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Cartões de teste</h3>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        <div className="modal-body">
          <p className="hint" style={{ marginTop: 0 }}>
            Use no checkout hosted/Elements. Validade: qualquer data futura · CVC:
            3 dígitos · CEP: qualquer. Clique no número pra copiar.
          </p>
          <table className="data-table">
            <thead>
              <tr>
                <th>Categoria</th>
                <th>Número</th>
                <th>O que acontece</th>
              </tr>
            </thead>
            <tbody>
              {TEST_CARD_GROUPS.flatMap((g) =>
                g.cards.map((c) => (
                  <tr key={c.number}>
                    <td>{g.title}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <code
                        onClick={() => copy(c.number)}
                        style={{ cursor: "pointer" }}
                        title="Clique pra copiar"
                      >
                        {c.number}
                      </code>
                      {copied === c.number && (
                        <span className="muted small"> copiado</span>
                      )}
                    </td>
                    <td>{c.effect}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <p className="hint" style={{ marginBottom: 0 }}>
            Inline (ex: Doação) usa <code>pm_card_visa</code> no backend, sem digitar
            cartão. Lista completa em docs/TEST-CARDS.md.
          </p>
        </div>
      </div>
    </div>
  );
}

// Carrega o stripe.js uma vez, com a publishable key vinda do backend.
let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise(): Promise<Stripe | null> {
  if (stripePromise) return stripePromise;
  stripePromise = api<{ stripe_publishable_key: string }>(
    "/api/config/public"
  ).then((r) => {
    if (!r.ok) throw new Error("config_public_failed");
    return loadStripe(r.body.stripe_publishable_key);
  });
  return stripePromise;
}

type CatalogProduct = {
  id: string;
  name: string;
  description: string;
  feature_key: string;
  amount_cents: number;
  currency: string;
  mode: "one-time" | "subscription";
  checkout_mode: "inline" | "hosted" | "elements";
  recurring_interval?: "day" | "week" | "month" | "year";
  stripe_price_id?: string;
  payment_method_types: string[];
  // Bumps configurados via admin (vazio = fallback automático "todos elegíveis").
  // Upsells aqui é só passthrough — consumido na Success page após compra.
  bumps?: string[];
  upsells?: string[];
  // Quando true, mesmo user pode comprar várias vezes (doação, coins, créditos).
  // Default false — storefront/checkout-sessions bloqueiam duplicata.
  allow_repurchase?: boolean;
};

const METHOD_LABEL: Record<string, string> = {
  card: "Cartão",
  pix: "Pix",
  boleto: "Boleto",
};

type Status =
  | { kind: "idle" }
  | { kind: "running"; productId: string; message: string }
  | { kind: "success"; productId: string; message: string }
  | { kind: "error"; productId: string; message: string };

function intervalLabel(interval?: string): string {
  switch (interval) {
    case "month":
      return "/mês";
    case "year":
      return "/ano";
    case "week":
      return "/semana";
    case "day":
      return "/dia";
    default:
      return "";
  }
}

type Activation = { featureKey: string; status: string };

export function Storefront() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  // bumpsByProduct[productId] = Set de feature_keys de bumps selecionados.
  // Só aplica pro card que tá sendo comprado; reseta após cada compra.
  const [bumpsByProduct, setBumpsByProduct] = useState<
    Record<string, Set<string>>
  >({});
  // campaignByProduct[productId] = { campaign_id, promo_code } digitados no card.
  // Metadados canônicos que o app externo normalmente passa via API; aqui o
  // vendedor preenche na hora pra rastrear campanha/promo. Vazios são omitidos.
  const [campaignByProduct, setCampaignByProduct] = useState<
    Record<
      string,
      { campaign_id: string; promo_code: string; affiliate_code: string }
    >
  >({});
  // Cards com o painel de campanha/promo aberto.
  const [campaignOpen, setCampaignOpen] = useState<Set<string>>(new Set());
  const [showTestCards, setShowTestCards] = useState(false);
  // Set de feature_keys ativas pro user logado. Evita duplo-compra: produto que
  // o user já possui não aparece como bump elegível e o botão principal vira
  // "Já ativo" desabilitado.
  const [activeFeatures, setActiveFeatures] = useState<Set<string>>(new Set());
  // Checkout via Stripe Elements (campos embutidos no site). Guarda o produto +
  // o client_secret do PaymentIntent criado pra montar o PaymentElement no modal.
  const [elementsCheckout, setElementsCheckout] = useState<{
    product: CatalogProduct;
    clientSecret: string;
  } | null>(null);
  // Link de oferta (?offer=): redireciona direto pro checkout dirigido pela oferta.
  const [offerRedirecting, setOfferRedirecting] = useState(false);

  useEffect(() => {
    api<{ data: CatalogProduct[] }>("/api/catalog").then((r) => {
      if (r.ok) setCatalog(r.body.data);
      setLoading(false);
    });
    // Captura ?offer= no mount (antes do gate de login) pra nao perder no bounce.
    readPendingOffer();
  }, []);

  // Link de oferta: quando logado e ha oferta pendente, cria a sessao dirigida
  // pela oferta (price + cupom + brand vem do servidor) e redireciona. One-shot.
  useEffect(() => {
    if (!user) return;
    const code = readPendingOffer();
    if (!code) return;
    let cancelled = false;
    setOfferRedirecting(true);
    const origin = window.location.origin;
    const affiliate = readAffiliateCode();
    api<{ url: string }>("/api/checkout-sessions", {
      method: "POST",
      body: JSON.stringify({
        offer: code,
        customer: user.stripeCustomerId ?? undefined,
        success_url: `${origin}/loja/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${origin}/loja/cancel`,
        metadata: {
          user_id: String(user.id),
          ...(affiliate ? { affiliate_code: affiliate } : {}),
        },
      }),
    }).then((r) => {
      if (cancelled) return;
      clearPendingOffer();
      if (r.ok && r.body?.url) {
        window.location.href = r.body.url;
      } else {
        setOfferRedirecting(false);
        setStatus({
          kind: "error",
          productId: "",
          message: `Oferta indisponível (HTTP ${r.status})`,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api<{ data: Activation[] }>(`/api/users/${user.id}/activations`).then((r) => {
      if (!r.ok) return;
      setActiveFeatures(
        new Set(
          r.body.data
            .filter((a) => a.status === "active")
            .map((a) => a.featureKey)
        )
      );
    });
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;

  // Bumps elegíveis (hosted, ≠ próprio produto):
  //  - One-time: sempre elegível em qualquer card hosted (mix em mode=subscription
  //    vira InvoiceItem na primeira fatura — ver handler invoice.paid).
  //  - Subscription: só elegível em card sub do MESMO recurring_interval. Stripe
  //    não aceita misturar intervalos numa única sub (sub única só com items que
  //    compartilham interval). Em card one-time, sub bump não é oferecido
  //    (cenário "one-time + sub bump" não é suportado em session única).
  //
  // Se admin configurou bumps explícitos via /api/admin/feature-catalog/:fk/bumps,
  // narrowing a essa lista (mantendo o filtro de interval que é constraint Stripe).
  // Lista vazia → fallback automático (todos elegíveis) pra não quebrar instalações
  // sem config admin.
  const eligibleBumpsFor = (p: CatalogProduct): CatalogProduct[] => {
    if (p.checkout_mode !== "hosted") return [];
    const configured = (p.bumps ?? []).length > 0 ? new Set(p.bumps) : null;
    return catalog.filter((b) => {
      if (b.id === p.id) return false;
      if (b.checkout_mode !== "hosted") return false;
      if (configured && !configured.has(b.feature_key)) return false;
      // Filtra features que o user já tem — evita duplo-compra silenciosa.
      // Pra sub esse caso é especialmente importante: Stripe não aceita 2 items
      // do mesmo price na mesma sub e o cliente pode acabar com 2 subs paralelas.
      if (activeFeatures.has(b.feature_key)) return false;
      if (b.mode === "one-time") return true;
      // b.mode === "subscription" → exige p ser sub do mesmo interval e ter Price.
      return (
        p.mode === "subscription" &&
        !!b.stripe_price_id &&
        !!b.recurring_interval &&
        b.recurring_interval === p.recurring_interval
      );
    });
  };

  const toggleBump = (productId: string, featureKey: string) => {
    setBumpsByProduct((prev) => {
      const current = new Set(prev[productId] ?? []);
      if (current.has(featureKey)) current.delete(featureKey);
      else current.add(featureKey);
      return { ...prev, [productId]: current };
    });
  };

  const toggleCampaign = (productId: string) => {
    setCampaignOpen((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const setCampaignField = (
    productId: string,
    field: "campaign_id" | "promo_code" | "affiliate_code",
    value: string
  ) => {
    setCampaignByProduct((prev) => ({
      ...prev,
      [productId]: {
        campaign_id: prev[productId]?.campaign_id ?? "",
        promo_code: prev[productId]?.promo_code ?? "",
        affiliate_code: prev[productId]?.affiliate_code ?? "",
        [field]: value,
      },
    }));
  };

  // Monta o bloco de metadata canônico do card. campaign_id/promo_code só entram
  // se preenchidos; source vira "campaign" quando há campaign_id (senão "storefront").
  const buildMetadata = (p: CatalogProduct): Record<string, string> => {
    const c = campaignByProduct[p.id];
    const campaignId = c?.campaign_id?.trim();
    const promoCode = c?.promo_code?.trim();
    // Código digitado no card tem prioridade; senão cai no ?aff= da URL/localStorage.
    const affiliateCode = c?.affiliate_code?.trim() || readAffiliateCode();
    return {
      brand: "icl",
      source: campaignId ? "campaign" : "storefront",
      feature_key: p.feature_key,
      product_key: p.feature_key,
      user_id: String(user.id),
      ...(campaignId ? { campaign_id: campaignId } : {}),
      ...(promoCode ? { promo_code: promoCode } : {}),
      ...(affiliateCode ? { affiliate_code: affiliateCode } : {}),
    };
  };

  const buy = async (p: CatalogProduct) => {
    if (!user.stripeCustomerId) {
      setStatus({
        kind: "error",
        productId: p.id,
        message: "User não tem Stripe Customer linkado",
      });
      return;
    }

    if (p.checkout_mode === "elements") {
      // Checkout custom: cria o PaymentIntent (sem confirmar) e abre o
      // PaymentElement embutido. O webhook payment_intent.succeeded ativa a
      // feature (por isso o metadata canônico vai no PI, igual aos outros).
      setStatus({
        kind: "running",
        productId: p.id,
        message: "Preparando pagamento...",
      });
      const r = await api<{ client_secret: string }>("/api/payment-intents", {
        method: "POST",
        body: JSON.stringify({
          amount: p.amount_cents,
          currency: p.currency,
          customer: user.stripeCustomerId,
          automatic_payment_methods: { enabled: true },
          metadata: buildMetadata(p),
        }),
      });
      if (!r.ok || !r.body?.client_secret) {
        setStatus({
          kind: "error",
          productId: p.id,
          message: `Falhou (HTTP ${r.status})`,
        });
        return;
      }
      setStatus({ kind: "idle" });
      setElementsCheckout({ product: p, clientSecret: r.body.client_secret });
      return;
    }

    if (p.checkout_mode === "inline") {
      // One-time via PaymentIntent confirm inline (sandbox-friendly)
      setStatus({
        kind: "running",
        productId: p.id,
        message: "Processando pagamento...",
      });
      const r = await api<{ id: string; status: string }>(
        "/api/payment-intents",
        {
          method: "POST",
          body: JSON.stringify({
            amount: p.amount_cents,
            currency: p.currency,
            customer: user.stripeCustomerId,
            description: p.name,
            payment_method: "pm_card_visa",
            payment_method_types: ["card"],
            confirm: true,
            metadata: buildMetadata(p),
          }),
        }
      );
      if (!r.ok) {
        setStatus({
          kind: "error",
          productId: p.id,
          message: `Falhou (HTTP ${r.status})`,
        });
        return;
      }
      setStatus({
        kind: "success",
        productId: p.id,
        message: `Pago! Webhook ativando feature... (${r.body.id})`,
      });
      // After a short delay redirect to "Minha Conta" so the activation is visible
      setTimeout(() => navigate("/conta"), 2000);
      return;
    }

    // Hosted checkout: cria checkout session e redireciona
    setStatus({
      kind: "running",
      productId: p.id,
      message: "Abrindo Stripe Checkout...",
    });

    const origin = window.location.origin;

    // Linha do produto principal (sub usa Price pré-criado; one-time usa price_data inline)
    const lineItems: any[] =
      p.mode === "subscription" && p.stripe_price_id
        ? [{ price: p.stripe_price_id, quantity: 1 }]
        : [
            {
              quantity: 1,
              price_data: {
                currency: p.currency,
                product_data: {
                  name: p.name,
                  description: p.description,
                  // feature_key vai no Product pra webhook descobrir o que ativar
                  // por line_item (sob order bump, há N features pra ativar).
                  metadata: { feature_key: p.feature_key },
                },
                unit_amount: p.amount_cents,
              },
            },
          ];

    // Order bump: adiciona line_items adicionais marcados pelo usuário.
    // One-time bumps usam price_data inline; sub bumps usam Price pré-criado
    // (Stripe Checkout exige Price-existente pra recurring em mode=subscription
    // — price_data com recurring funciona mas ressuscita um Price ad-hoc por
    // sessão, perdendo histórico/versionamento. Reusar price_id é mais limpo).
    const selectedBumps = bumpsByProduct[p.id] ?? new Set<string>();
    for (const bumpKey of selectedBumps) {
      const bump = catalog.find((b) => b.feature_key === bumpKey);
      if (!bump) continue;
      if (bump.mode === "subscription" && bump.stripe_price_id) {
        lineItems.push({ price: bump.stripe_price_id, quantity: 1 });
      } else {
        lineItems.push({
          quantity: 1,
          price_data: {
            currency: bump.currency,
            product_data: {
              name: bump.name,
              description: bump.description,
              metadata: {
                feature_key: bump.feature_key,
                order_bump: "true",
              },
            },
            unit_amount: bump.amount_cents,
          },
        });
      }
    }

    const r = await api<{ url: string }>("/api/checkout-sessions", {
      method: "POST",
      body: JSON.stringify({
        mode: p.mode === "subscription" ? "subscription" : "payment",
        customer: user.stripeCustomerId,
        payment_method_types: p.payment_method_types,
        line_items: lineItems,
        // Salva o cartão pra upsell pós-compra (off_session). Sub mode já salva
        // por default; aplica só em payment (one-time hosted).
        ...(p.mode !== "subscription" && {
          payment_intent_data: { setup_future_usage: "off_session" },
        }),
        success_url: `${origin}/loja/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${origin}/loja/cancel`,
        metadata: buildMetadata(p),
      }),
    });
    if (!r.ok || !r.body?.url) {
      setStatus({
        kind: "error",
        productId: p.id,
        message: `Falhou (HTTP ${r.status})`,
      });
      return;
    }
    window.location.href = r.body.url;
  };

  if (offerRedirecting)
    return <p className="loading">Preparando seu checkout...</p>;
  if (loading) return <p className="loading">Carregando catálogo...</p>;

  return (
    <div className="storefront">
      <div className="test-env-banner" role="alert">
        <strong>Ambiente de teste</strong> — esta loja é uma demonstração. Os
        produtos não são reais, nenhuma cobrança verdadeira é feita e nenhum
        pedido é processado. Use apenas cartões de teste (ex:{" "}
        <code>4242 4242 4242 4242</code>).
        {" "}
        <button
          type="button"
          className="ghost-btn small"
          onClick={() => setShowTestCards(true)}
        >
          Ver cartões de teste
        </button>
      </div>
      <header className="page-header">
        <h1>Loja</h1>
        <p className="hint">
          Produtos de demo. <strong>Inline</strong> usa cartão de teste fake e
          confirma na hora. <strong>Hosted</strong> redireciona pro Stripe
          Checkout (use o cartão <code>4242 4242 4242 4242</code>).
        </p>
      </header>

      <div className="product-grid">
        {catalog.map((p) => {
          const isThisRunning =
            status.kind === "running" && status.productId === p.id;
          const showStatus =
            status.kind !== "idle" && status.productId === p.id;
          // Produtos com allow_repurchase=true (doação, coins, créditos) seguem
          // mostrando o botão habilitado mesmo se o user já comprou — admin
          // marca a flag por produto em /admin → Catálogo → Editar.
          const allowRepurchase = !!p.allow_repurchase;
          const alreadyOwned =
            !allowRepurchase && activeFeatures.has(p.feature_key);
          return (
            <div key={p.id} className="product-card">
              <div className="product-badges">
                <span className={`tag tag-${p.mode}`}>
                  {p.mode === "subscription" ? "Recorrente" : "Único"}
                </span>
                <span className={`tag tag-${p.checkout_mode}`}>
                  {p.checkout_mode === "inline"
                    ? "Inline"
                    : p.checkout_mode === "elements"
                      ? "Elements"
                      : "Hosted"}
                </span>
                {alreadyOwned && (
                  <span className="tag tag-owned">Você já tem</span>
                )}
              </div>
              <h2>{p.name}</h2>
              <p className="product-desc">{p.description}</p>
              <p className="product-price">
                {brl(p.amount_cents, p.currency)}
                <small>{intervalLabel(p.recurring_interval)}</small>
              </p>
              <p className="product-methods">
                {p.payment_method_types
                  .map((m) => METHOD_LABEL[m] ?? m)
                  .join(" · ")}
              </p>
              {eligibleBumpsFor(p).length > 0 && (
                <div className="product-bumps">
                  <div className="product-bumps-title">Adicione na mesma compra:</div>
                  {eligibleBumpsFor(p).map((b) => {
                    const checked =
                      bumpsByProduct[p.id]?.has(b.feature_key) ?? false;
                    return (
                      <label key={b.id} className="product-bump-row">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleBump(p.id, b.feature_key)}
                          disabled={status.kind === "running"}
                        />
                        <span>
                          {b.name} — {brl(b.amount_cents, b.currency)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              <button
                type="button"
                className="product-campaign-toggle"
                onClick={() => toggleCampaign(p.id)}
                disabled={status.kind === "running"}
              >
                {campaignOpen.has(p.id)
                  ? "− Campanha / promoção"
                  : "+ Campanha / promoção"}
              </button>
              {campaignOpen.has(p.id) && (
                <div className="product-campaign">
                  <label className="product-campaign-field">
                    <span>ID da campanha</span>
                    <input
                      type="text"
                      placeholder="ex: black_friday_2026"
                      value={campaignByProduct[p.id]?.campaign_id ?? ""}
                      onChange={(e) =>
                        setCampaignField(p.id, "campaign_id", e.target.value)
                      }
                      disabled={status.kind === "running"}
                    />
                  </label>
                  <label className="product-campaign-field">
                    <span>Código promocional</span>
                    <input
                      type="text"
                      placeholder="ex: PROMO20"
                      value={campaignByProduct[p.id]?.promo_code ?? ""}
                      onChange={(e) =>
                        setCampaignField(p.id, "promo_code", e.target.value)
                      }
                      disabled={status.kind === "running"}
                    />
                  </label>
                  <label className="product-campaign-field">
                    <span>Código de afiliado</span>
                    <input
                      type="text"
                      placeholder="ex: AFF1 (repasse de comissão)"
                      value={campaignByProduct[p.id]?.affiliate_code ?? ""}
                      onChange={(e) =>
                        setCampaignField(p.id, "affiliate_code", e.target.value)
                      }
                      disabled={status.kind === "running"}
                    />
                  </label>
                </div>
              )}
              <button
                onClick={() => buy(p)}
                disabled={status.kind === "running" || alreadyOwned}
                title={alreadyOwned ? "Você já tem esta feature ativa" : undefined}
              >
                {isThisRunning
                  ? "..."
                  : alreadyOwned
                    ? "Já ativo"
                    : p.mode === "subscription"
                      ? "Assinar"
                      : p.feature_key === "bolsa_donation"
                        ? "Doar"
                        : "Comprar"}
              </button>
              {showStatus && (
                <p className={`product-status status-${status.kind}`}>
                  {status.message}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {showTestCards && (
        <TestCardsModal onClose={() => setShowTestCards(false)} />
      )}

      {elementsCheckout && (
        <Elements
          stripe={getStripePromise()}
          options={{
            clientSecret: elementsCheckout.clientSecret,
            appearance: { theme: "stripe" },
          }}
        >
          <ElementsCheckoutModal
            product={elementsCheckout.product}
            onClose={() => setElementsCheckout(null)}
            onPaid={() => {
              setElementsCheckout(null);
              navigate("/conta");
            }}
          />
        </Elements>
      )}
    </div>
  );
}

// Modal de checkout custom com Stripe Elements. Renderiza o PaymentElement
// (campos embutidos: cartão + métodos habilitados na conta) e confirma o
// PaymentIntent sem sair do site. redirect:"if_required" mantém cartão inline;
// métodos assíncronos (Pix/boleto) redirecionam pro return_url. O webhook
// payment_intent.succeeded ativa a feature.
function ElementsCheckoutModal({
  product,
  onClose,
  onPaid,
}: {
  product: CatalogProduct;
  onClose: () => void;
  onPaid: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const priceLabel = brl(product.amount_cents, product.currency);

  const pay = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: err, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/conta` },
      redirect: "if_required",
    });
    if (err) {
      setError(err.message ?? "Falha no pagamento");
      setSubmitting(false);
      return;
    }
    if (
      paymentIntent &&
      (paymentIntent.status === "succeeded" ||
        paymentIntent.status === "processing")
    ) {
      onPaid();
      return;
    }
    setSubmitting(false);
  };

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            Pagar — {product.name}{" "}
            <span className="muted" style={{ fontWeight: "normal" }}>
              {priceLabel}
            </span>
          </h3>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        <form className="modal-body" onSubmit={pay}>
          <PaymentElement />
          {error && (
            <p className="error" style={{ marginTop: "0.75em" }}>
              {error}
            </p>
          )}
          <div className="form-actions" style={{ marginTop: "1em" }}>
            <button
              type="submit"
              className="primary-btn"
              disabled={!stripe || submitting}
            >
              {submitting ? "Processando..." : `Pagar ${priceLabel}`}
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
